import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import PageSeo from '../../components/PageSeo';
import { 
  ChevronRight, MapPin, Tag, Calendar, Shield, ArrowRight, 
  Filter, Grid, List, SlidersHorizontal, TrendingUp, Package
} from 'lucide-react';
import { getCurrencyByLocation, formatPrice } from '../../utils/currency';
import { getFallbackImage } from '../../utils/fallbackImages';

export default function ShopCategory() {
  const { categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { API } = useContext(AppContext);
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  
  // Filters
  const [listingType, setListingType] = useState(searchParams.get('type') || 'all');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'recent');

  useEffect(() => {
    if (categorySlug) {
      fetchCategoryData();
    }
  }, [categorySlug, listingType, sortBy]);

  const fetchCategoryData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (listingType !== 'all') params.set('listing_type', listingType);
      
      const response = await axios.get(
        `${API}/products/category/${categorySlug}/products?${params.toString()}`
      );
      
      setCategory(response.data.category);
      setProducts(response.data.products || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching category:', error);
    } finally {
      setLoading(false);
    }
  };

  // Sort products
  const sortedProducts = [...products].sort((a, b) => {
    if (sortBy === 'price_asc') {
      const priceA = a.price_used || a.price_new || a.price_rental || 999999;
      const priceB = b.price_used || b.price_new || b.price_rental || 999999;
      return priceA - priceB;
    }
    if (sortBy === 'price_desc') {
      const priceA = a.price_used || a.price_new || a.price_rental || 0;
      const priceB = b.price_used || b.price_new || b.price_rental || 0;
      return priceB - priceA;
    }
    // Default: recent
    return new Date(b.created_at) - new Date(a.created_at);
  });

  // Schema.org JSON-LD for category page
  const categorySchema = category ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name_fr} - Matériel Géotechnique`,
    "description": category.description_fr || `Découvrez notre sélection de ${category.name_fr?.toLowerCase()} pour vos chantiers géotechniques. Achat, location, occasion.`,
    "url": `https://geots.consulting/materiel/${categorySlug}`,
    "numberOfItems": totalCount,
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": totalCount,
      "itemListElement": sortedProducts.slice(0, 10).map((product, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Product",
          "name": product.name_fr,
          "url": `https://geots.consulting/materiel/${categorySlug}/${product.slug || product.id}`,
          "image": product.images?.[0],
          "brand": product.brand ? { "@type": "Brand", "name": product.brand } : undefined,
          "offers": product.show_price !== false ? {
            "@type": "Offer",
            "price": product.price_used || product.price_new || product.price_rental,
            "priceCurrency": "EUR",
            "availability": product.availability_status === 'available' 
              ? "https://schema.org/InStock" 
              : "https://schema.org/OutOfStock"
          } : undefined
        }
      }))
    }
  } : null;

  // Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Matériel", "item": "https://geots.consulting/materiel" },
      { "@type": "ListItem", "position": 3, "name": category?.name_fr || categorySlug, "item": `https://geots.consulting/materiel/${categorySlug}` }
    ]
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#635BFF]"></div>
      </div>
    );
  }

  if (!category) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Catégorie non trouvée</h1>
          <p className="text-gray-500 mb-4">Cette catégorie n&apos;existe pas ou a été supprimée.</p>
          <Link to="/shop">
            <Button>Retour à la boutique</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* SEO */}
      <Helmet>
        <title>{category.name_fr} | Achat & Location Matériel Géotechnique | GEOTS</title>
        <meta 
          name="description" 
          content={`${category.name_fr} : ${totalCount} équipements disponibles. Achat, location et occasion de matériel géotechnique professionnel. Livraison France & Afrique.`} 
        />
        <link rel="canonical" href={`https://geots.consulting/materiel/${categorySlug}`} />
        <meta property="og:title" content={`${category.name_fr} | GEOTS Consulting`} />
        <meta property="og:description" content={category.description_fr || `Découvrez notre sélection de ${category.name_fr?.toLowerCase()}`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://geots.consulting/materiel/${categorySlug}`} />
        <script type="application/ld+json">{JSON.stringify(categorySchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-12 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/shop" className="hover:text-white">Matériel</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium">{category.name_fr}</span>
          </nav>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
              <Package className="w-4 h-4 text-[#00D4FF]" />
              <span className="text-sm font-medium">{totalCount} équipement{totalCount > 1 ? 's' : ''} disponible{totalCount > 1 ? 's' : ''}</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {category.name_fr}
            </h1>

            {category.description_fr && (
              <p className="text-lg md:text-xl text-gray-300 mb-6 max-w-2xl">
                {category.description_fr}
              </p>
            )}

            {/* Quick Stats */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Tag className="w-5 h-5 text-green-400" />
                <span>Neuf & Occasion</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <Calendar className="w-5 h-5 text-[#00D4FF]" />
                <span>Location possible</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-lg">
                <MapPin className="w-5 h-5 text-yellow-400" />
                <span>France & Afrique</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Left: Listing Type Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 hidden sm:inline">Type :</span>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'all', label: 'Tous' },
                  { value: 'neuf', label: '✨ Neuf' },
                  { value: 'occasion', label: '🔄 Occasion' },
                  { value: 'location', label: '📅 Location' },
                ].map(type => (
                  <button
                    key={type.value}
                    onClick={() => {
                      setListingType(type.value);
                      setSearchParams(prev => {
                        if (type.value === 'all') prev.delete('type');
                        else prev.set('type', type.value);
                        return prev;
                      });
                    }}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      listingType === type.value
                        ? 'bg-[#635BFF] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Sort & View */}
            <div className="flex items-center gap-3">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px]">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Trier par" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Plus récents</SelectItem>
                  <SelectItem value="price_asc">Prix croissant</SelectItem>
                  <SelectItem value="price_desc">Prix décroissant</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden sm:flex border rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 ${viewMode === 'grid' ? 'bg-[#635BFF] text-white' : 'bg-white text-gray-600'}`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 ${viewMode === 'list' ? 'bg-[#635BFF] text-white' : 'bg-white text-gray-600'}`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container mx-auto px-4 py-8">
        {sortedProducts.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h2 className="text-xl font-semibold text-gray-700 mb-2">Aucun produit trouvé</h2>
            <p className="text-gray-500 mb-4">
              Aucun équipement ne correspond à vos critères dans cette catégorie.
            </p>
            <Button onClick={() => setListingType('all')}>
              Voir tous les produits
            </Button>
          </div>
        ) : (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
          }>
            {sortedProducts.map(product => {
              const productName = product.name_fr;
              const fallbackImg = getFallbackImage(productName);
              const validImages = (product.images || []).filter(img => img && img.trim() !== '');
              const imgSrc = validImages[0] || fallbackImg;
              const currency = getCurrencyByLocation(product.location);
              
              // Determine listing badge
              const getListingBadge = () => {
                const type = product.listing_type;
                if (type === 'neuf') return { label: 'NEUF', color: 'bg-blue-500', icon: '✨' };
                if (type === 'occasion') return { label: 'OCCASION', color: 'bg-amber-500', icon: '🔄' };
                if (type === 'location') return { label: 'LOCATION', color: 'bg-purple-500', icon: '📅' };
                if (product.price_used && !product.price_new) return { label: 'OCCASION', color: 'bg-amber-500', icon: '🔄' };
                if (product.price_new) return { label: 'NEUF', color: 'bg-blue-500', icon: '✨' };
                return null;
              };
              const listingBadge = getListingBadge();

              // Main price
              const getMainPrice = () => {
                if (product.show_price === false) return { type: 'contact', label: 'Prix sur demande' };
                if (product.price_used) return { type: 'used', value: product.price_used, label: 'Occasion' };
                if (product.price_new) return { type: 'new', value: product.price_new, label: 'Neuf' };
                if (product.price_rental) return { type: 'rental', value: product.price_rental, label: '/jour' };
                return { type: 'quote', label: 'Sur devis' };
              };
              const mainPrice = getMainPrice();

              // Product URL (SEO-friendly)
              const productUrl = product.slug 
                ? `/materiel/${categorySlug}/${product.slug}`
                : `/product/${product.id}`;

              if (viewMode === 'list') {
                return (
                  <Link key={product.id} to={productUrl}>
                    <Card className="group hover:shadow-lg transition-all overflow-hidden">
                      <CardContent className="p-0 flex flex-col sm:flex-row">
                        <div className="sm:w-48 h-40 sm:h-auto relative overflow-hidden flex-shrink-0">
                          <img 
                            src={imgSrc} 
                            alt={productName}
                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                            onError={(e) => { e.target.onerror = null; e.target.src = fallbackImg; }}
                          />
                          {listingBadge && (
                            <span className={`absolute top-2 left-2 ${listingBadge.color} text-white px-2 py-1 rounded text-xs font-bold`}>
                              {listingBadge.icon} {listingBadge.label}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 p-4">
                          <div className="flex flex-col h-full justify-between">
                            <div>
                              {product.brand && (
                                <span className="text-xs text-[#635BFF] font-semibold uppercase">{product.brand}</span>
                              )}
                              <h3 className="font-bold text-lg text-[#0A2540] group-hover:text-[#635BFF] transition-colors">
                                {productName}
                              </h3>
                              {product.location && (
                                <p className="text-sm text-gray-500 flex items-center mt-1">
                                  <MapPin className="w-3 h-3 mr-1" /> {product.location}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center justify-between mt-3">
                              {mainPrice.type === 'contact' || mainPrice.type === 'quote' ? (
                                <span className="text-[#635BFF] font-semibold">{mainPrice.label}</span>
                              ) : (
                                <span className="text-xl font-bold text-[#0A2540]">
                                  {formatPrice(mainPrice.value, currency)} 
                                  <span className="text-sm text-gray-500 font-normal ml-1">
                                    {mainPrice.type === 'rental' ? '/jour HT' : 'HT'}
                                  </span>
                                </span>
                              )}
                              <ArrowRight className="w-5 h-5 text-[#635BFF] opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                );
              }

              return (
                <Link key={product.id} to={productUrl}>
                  <Card className="group h-full hover:shadow-xl transition-all duration-300 overflow-hidden border-0 shadow-md">
                    <CardContent className="p-0 relative">
                      {/* Badges */}
                      <div className="absolute top-3 left-3 z-10 flex flex-col gap-2">
                        {listingBadge && (
                          <span className={`${listingBadge.color} text-white px-2 py-1 rounded text-xs font-bold shadow-lg`}>
                            {listingBadge.icon} {listingBadge.label}
                          </span>
                        )}
                      </div>
                      
                      {product.featured && (
                        <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-white px-2 py-1 rounded text-xs font-bold shadow-lg">
                          ⭐ Vedette
                        </div>
                      )}
                      
                      {/* Image */}
                      <div className="relative overflow-hidden">
                        <img 
                          src={imgSrc} 
                          alt={productName}
                          className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105"
                          onError={(e) => { e.target.onerror = null; e.target.src = fallbackImg; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        {product.brand && (
                          <span className="text-xs text-[#635BFF] font-semibold uppercase tracking-wide">
                            {product.brand}
                          </span>
                        )}
                        <h3 className="font-bold text-lg text-[#0A2540] line-clamp-1 group-hover:text-[#635BFF] transition-colors">
                          {productName}
                        </h3>
                        
                        {product.location && (
                          <p className="text-xs text-gray-500 mb-2 flex items-center">
                            <MapPin className="w-3 h-3 mr-1 text-[#635BFF]" />
                            {product.location}
                          </p>
                        )}
                        
                        {/* Price */}
                        <div className="pt-3 border-t border-gray-100">
                          {mainPrice.type === 'contact' || mainPrice.type === 'quote' ? (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-500">{mainPrice.label}</span>
                              <span className="bg-[#635BFF] text-white px-3 py-1 rounded-full text-sm font-semibold">
                                Nous contacter
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-2xl font-bold text-[#0A2540]">
                                  {formatPrice(mainPrice.value, currency)}
                                </span>
                                <span className="text-xs text-gray-500 ml-1">
                                  {mainPrice.type === 'rental' ? '/jour HT' : 'HT'}
                                </span>
                              </div>
                              <span className={`text-xs px-2 py-1 rounded ${
                                mainPrice.type === 'new' ? 'bg-blue-100 text-blue-700' :
                                mainPrice.type === 'used' ? 'bg-amber-100 text-amber-700' :
                                'bg-purple-100 text-purple-700'
                              }`}>
                                {mainPrice.label}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* Hover CTA */}
                        <div className="mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="bg-[#635BFF] text-white text-center py-2 rounded-lg text-sm font-semibold">
                            Voir les détails →
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* SEO Content Section */}
      <div className="bg-white border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4">
              {category.name_fr} pour professionnels de la géotechnique
            </h2>
            <p className="text-gray-600 mb-6">
              GEOTS Consulting vous propose une sélection de {category.name_fr?.toLowerCase()} 
              pour vos chantiers de fondations spéciales, études géotechniques et travaux de forage. 
              Que vous recherchiez du matériel neuf, d&apos;occasion ou en location, nos équipements 
              sont vérifiés et conformes aux normes européennes.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 not-prose">
              <div className="bg-gray-50 rounded-lg p-4">
                <TrendingUp className="w-8 h-8 text-[#635BFF] mb-2" />
                <h3 className="font-semibold text-[#0A2540] mb-1">Qualité vérifiée</h3>
                <p className="text-sm text-gray-600">
                  Tous nos équipements sont inspectés et certifiés conformes.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <Shield className="w-8 h-8 text-green-500 mb-2" />
                <h3 className="font-semibold text-[#0A2540] mb-1">Garantie incluse</h3>
                <p className="text-sm text-gray-600">
                  Bénéficiez d&apos;une garantie sur le matériel neuf et reconditionné.
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <MapPin className="w-8 h-8 text-[#00D4FF] mb-2" />
                <h3 className="font-semibold text-[#0A2540] mb-1">Livraison internationale</h3>
                <p className="text-sm text-gray-600">
                  Expédition France métropolitaine, DOM-TOM et Afrique francophone.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] py-12">
        <div className="container mx-auto px-4 text-center text-white">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Vous ne trouvez pas ce que vous cherchez ?
          </h2>
          <p className="text-white/80 mb-6 max-w-2xl mx-auto">
            Publiez une demande de matériel et recevez des propositions de nos fournisseurs vérifiés.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/requests">
              <Button size="lg" className="bg-white text-[#635BFF] hover:bg-gray-100">
                Voir les demandes
              </Button>
            </Link>
            <Link to="/materiel/demande">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Publier une demande
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
